// Build My Trip's "This guide's places" source (2026-10-16): the guide's own
// places become the trip.
//   - suggestDestinations: a guide with no destinations of its own (a
//     Featured guide, an import, places added by hand) gets them suggested
//     from its places — grouped by city, in route order, days from the
//     places' visit lengths. The user checks the dates before building.
//   - assignToDestinations: each place goes to the destination in its city;
//     a place in no destination's city goes to the nearest one within
//     NEAR_KM, else is left out of this trip (it stays in the guide).
// Hotels are never visits (the schedule's day starts there — see
// dayStartTravel.js), and hidden places stay out.

import { isHotel, hotelCategoryIds } from "./dayStartTravel";

const MERGE_KM = 25; // two city names this close are one city ("Stadt Berlin" / "Berlin")
const NEAR_KM = 150;
const MIN_PLACES = 3; // a city needs this many places to be its own destination
const norm = (s) => String(s || "").trim().toLowerCase();
const km = (a, b) => {
  const r = Math.PI / 180;
  const x = (b.lng - a.lng) * r * Math.cos(((a.lat + b.lat) / 2) * r), y = (b.lat - a.lat) * r;
  return Math.sqrt(x * x + y * y) * 6371;
};
const at = (p) => ({ lat: parseFloat(p.Lat), lng: parseFloat(p.Longi) });
const centroid = (list) => ({
  lat: list.reduce((n, p) => n + parseFloat(p.Lat), 0) / list.length,
  lng: list.reduce((n, p) => n + parseFloat(p.Longi), 0) / list.length,
});
const mostCommon = (values) => {
  const counts = new Map();
  values.filter(Boolean).forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "";
};

// The guide's places a trip can be built from.
export function eligibleGuidePlaces(placesList, categories) {
  const hotelIds = hotelCategoryIds(categories);
  return (placesList || []).filter((p) =>
    p && !p.Hidden && (!p.PType || p.PType === "Icon") && !isHotel(p, hotelIds) &&
    Number.isFinite(parseFloat(p.Lat)) && Number.isFinite(parseFloat(p.Longi)) && !(parseFloat(p.Lat) === 0 && parseFloat(p.Longi) === 0));
}

// Groups → destinations. `firstDay` = a DateTime for the first day (any
// time); `pace` = Build My Trip's visit-length multiplier (1.3 = relaxed).
// Returns { destinations: [{ id, city, country, dateStart, dateEnd, placeCount }], outsideCount }.
export function suggestDestinations(places, { firstDay, startH = 9, endH = 20, pace = 1 }) {
  if (!places || places.length === 0 || !firstDay) return { destinations: [], outsideCount: 0 };
  // 1. by city name
  const byCity = new Map();
  places.forEach((p) => {
    const k = norm(p.City) || "?";
    if (!byCity.has(k)) byCity.set(k, []);
    byCity.get(k).push(p);
  });
  let groups = [...byCity.values()].map((list) => ({ list, c: centroid(list) }));
  // 2. merge near-identical cities into the bigger one
  groups.sort((a, b) => b.list.length - a.list.length);
  const merged = [];
  groups.forEach((g) => {
    const into = merged.find((m) => km(m.c, g.c) <= MERGE_KM);
    if (into) { into.list.push(...g.list); into.c = centroid(into.list); } else merged.push({ ...g });
  });
  // 3. big enough to be a destination (at least the biggest one)
  let dests = merged.filter((g) => g.list.length >= MIN_PLACES);
  if (dests.length === 0) dests = [merged[0]];
  const outsideCount = places.length - dests.reduce((n, g) => n + g.list.length, 0);
  // 4. route order: from the city of the guide's first place, nearest next
  const firstGroup = dests.find((g) => g.list.includes(places[0])) || dests[0];
  const ordered = [firstGroup];
  let left = dests.filter((g) => g !== firstGroup);
  while (left.length) {
    const cur = ordered[ordered.length - 1].c;
    left.sort((a, b) => km(cur, a.c) - km(cur, b.c));
    ordered.push(left.shift());
  }
  // 5. days from visit lengths; consecutive dates, each city starting the
  //    morning after the previous one ends (the destinations screen's default)
  const dayMinutes = Math.max(60, (endH - startH) * 60);
  let day = firstDay.startOf("day");
  const destinations = ordered.map((g, i) => {
    const minutes = g.list.reduce((n, p) => n + (Number(p.Duration) > 0 ? Number(p.Duration) : 90) * (pace || 1) + 20, 0);
    const days = Math.min(10, Math.max(1, Math.ceil(minutes / dayMinutes)));
    const dateStart = day.set({ hour: startH, minute: 0 });
    const dateEnd = day.plus({ days: days - 1 }).set({ hour: endH, minute: 0 });
    day = day.plus({ days });
    return {
      id: Date.now() + i,
      city: mostCommon(g.list.map((p) => p.City)),
      country: mostCommon(g.list.map((p) => p.Country)),
      dateStart,
      dateEnd,
      placeCount: g.list.length,
    };
  });
  return { destinations, outsideCount };
}

// places → destinations by index: { byDest: Map(index → places), outside: [places] }.
export function assignToDestinations(places, destinations) {
  const byDest = new Map(destinations.map((_, i) => [i, []]));
  const outside = [];
  const homeless = [];
  places.forEach((p) => {
    const i = destinations.findIndex((d) => norm(d.city) && norm(d.city) === norm(p.City));
    if (i !== -1) byDest.get(i).push(p);
    else homeless.push(p);
  });
  // where each destination is: its own places' centre
  const anchors = destinations.map((_, i) => (byDest.get(i).length ? centroid(byDest.get(i)) : null));
  // A city with enough places to be a destination of its own, but none in
  // this trip, was left out on purpose (removed from the suggestion): its
  // places stay out rather than being moved into a neighbouring city.
  const perCity = new Map();
  homeless.forEach((p) => perCity.set(norm(p.City), (perCity.get(norm(p.City)) || 0) + 1));
  homeless.forEach((p) => {
    if (norm(p.City) && perCity.get(norm(p.City)) >= MIN_PLACES) { outside.push(p); return; }
    let best = -1, bestKm = Infinity;
    anchors.forEach((a, i) => {
      if (!a) return;
      const d = km(at(p), a);
      if (d < bestKm) { bestKm = d; best = i; }
    });
    if (best !== -1 && bestKm <= NEAR_KM) byDest.get(best).push(p);
    else outside.push(p);
  });
  return { byDest, outside };
}

// A city visited more than once (Rome, Venice, Florence, Rome): every visit
// gets the city's whole list (2026-10-22). Building skips places already
// scheduled, so the first visit takes the places that fit and the next visit
// the rest — instead of the first visit getting them all and the return
// visit none. Returns a new Map; other destinations are unchanged.
export function shareRepeatVisits(byDest, destinations) {
  const out = new Map(byDest);
  const byCity = new Map();
  destinations.forEach((d, i) => {
    const c = norm(d.city);
    if (!c) return;
    if (!byCity.has(c)) byCity.set(c, []);
    byCity.get(c).push(i);
  });
  byCity.forEach((idxs) => {
    if (idxs.length < 2) return;
    const seen = new Set();
    const all = [];
    idxs.forEach((i) => (byDest.get(i) || []).forEach((p) => { if (!seen.has(p)) { seen.add(p); all.push(p); } }));
    idxs.forEach((i) => out.set(i, all.slice()));
  });
  return out;
}
